import React from 'react';
import { StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { REDESIGN_SPACING, REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import type { SurveySummary } from '@/types/residents/corkBoard';

interface Props {
  survey: SurveySummary;
  onPress: () => void;
}

function formatCountdown(expiresAt: string | null): string {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return '';
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return ` · closes in ${days}d`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return ` · closes in ${hours}h`;
  const mins = Math.max(1, Math.floor(ms / 60_000));
  return ` · closes in ${mins}m`;
}

export const SurveyCardCTA: React.FC<Props> = ({ survey, onPress }) => {
  const { colors } = useThemeColors();

  const label = survey.isClosed
    ? 'Closed'
    : survey.hasResponded
      ? 'You responded'
      : `Tap to respond · ${survey.questionCount} question${survey.questionCount === 1 ? '' : 's'}${formatCountdown(survey.expiresAt)}`;

  const iconName = survey.isClosed
    ? 'lock-closed-outline'
    : survey.hasResponded
      ? 'checkmark-circle-outline'
      : 'arrow-forward-circle-outline';

  return (
    <Hoverable
      hover="button"
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={[
        styles.cta,
        {
          backgroundColor: colors.newsfeed.announcementBg,
          borderColor: colors.newsfeed.announcementBorder,
        },
      ]}
    >
      <Ionicons name={iconName} size={18} color={colors.newsfeed.tagActiveText} />
      <Text
        style={[REDESIGN_TYPOGRAPHY.button, styles.label, { color: colors.newsfeed.tagActiveText }]}
        numberOfLines={1}
      >
        {label}
      </Text>
    </Hoverable>
  );
};

const styles = StyleSheet.create({
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: REDESIGN_SPACING.buttonRadius,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginTop: REDESIGN_SPACING.sectionGap,
  },
  label: { flexShrink: 1 },
});
